import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { onboardingClient, onboardingUtils } from "@v2/trpc/routes/onboarding";
import { projectRouter, projectUtils } from "@v2/trpc/routes/project";
import { userUtils } from "@v2/trpc/routes/user";
import { workspaceUtils } from "@v2/trpc/routes/workspace";
import { ReploError } from "schemas/reploError";
import { v4 as uuidv4 } from "uuid";

interface CreateProjectParams {
  shopName: string;
  workspaceId: string;
  hostingType: "shopify" | "replo";
  workspaceName?: string;
  shouldInvalidateUser?: boolean;
  isAgentEnabled?: boolean;
  shopifyIntegrationInstallationId?: string | null;
  onSuccess?: (projectId: string) => void | Promise<void>;
}

interface AnalyticsEventData {
  userId: string;
  stepId: string;
  stepType: string;
  stepData: {
    shopName: string;
    [key: string]: unknown;
  };
}

const formatShopNameToSubdomain = (shopName: string): string => {
  // Normalize accents: "Café" → "Cafe"
  let slug = shopName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^\d\sa-z-]/g, "") // Keep only letters, numbers, spaces, dashes
    .replace(/\s+/g, "-") // Spaces → "-"
    .replace(/-+/g, "-") // Collapse multiple "-"
    .replace(/^-+|-+$/g, "") // Trim leading/trailing "-"
    .slice(0, 63); // Max 63 chars (DNS label limit)

  // Must start and end with alphanumeric; if empty or invalid, fallback
  if (!/^[\da-z]([\da-z-]{0,61}[\da-z])?$/.test(slug)) {
    // Remove invalid start/end chars
    slug = slug.replace(/^[^\da-z]+|[^\da-z]+$/g, "");
  }

  // Still invalid or empty → fallback
  if (!slug) {
    slug = "shop";
  }

  return slug;
};

/**
 * The subdomain check is a direct client call, so no global handler reports its
 * failure; callers need to tell it apart from the create mutation's.
 */
export class SubdomainAvailabilityCheckError extends ReploError {}

export function useOnboardingCreateProject() {
  const logEvent = useLogAnalytics();
  const { mutateAsync: createProjectAsync, isPending: isCreatingProject } =
    projectRouter.create.useMutation();

  const createProject = async (params: CreateProjectParams) => {
    const {
      shopName,
      workspaceId,
      hostingType,
      workspaceName,
      shouldInvalidateUser = true,
      isAgentEnabled,
      shopifyIntegrationInstallationId,
      onSuccess,
    } = params;

    let subdomain = formatShopNameToSubdomain(shopName);
    if (hostingType === "replo") {
      const availabilityData = await onboardingClient.checkSubdomainAvailability
        .query({ subdomain })
        .catch((cause: unknown) => {
          throw new SubdomainAvailabilityCheckError({
            message: "Failed to check subdomain availability",
            cause,
          });
        });
      if (!availabilityData?.available) {
        subdomain = `${subdomain.slice(0, 58).replace(/-+$/, "")}-${uuidv4().slice(0, 4)}`;
      }
    }

    const result = await createProjectAsync({
      name: shopName.trim(),
      workspaceId: workspaceId,
      hostingType: hostingType,
      subdomain: hostingType === "shopify" ? null : subdomain.trim(),
      workspaceName: workspaceName?.slice(0, 255),
      isAgentEnabled,
      shopifyIntegrationInstallationId,
    });

    void workspaceUtils.getUserWorkspacesList.invalidate();
    void projectUtils.listWithStats.invalidate();
    void projectUtils.find.invalidate();

    if (shouldInvalidateUser) {
      void userUtils.get.invalidate();
    }

    void projectUtils.listGroupedByWorkspaceForUser.invalidate();
    void onboardingUtils.checkSubdomainAvailability.invalidate();

    if (result.project?.id) {
      await onSuccess?.(result.project.id);
    }

    return result;
  };

  const logOnboardingStepCompleted = (data: AnalyticsEventData) => {
    logEvent("onboarding.flow.step.completed", data);
  };

  return {
    createProject,
    isCreatingProject,
    logOnboardingStepCompleted,
  };
}
